/**
 * MobileShell — the phone-viewport replacement for AppLayout, mounted by the
 * same top-level `/*` catch-all (see RootShell in App.tsx). It inherits the
 * FULL provider stack (theme/toast/tenant/realtime/upload) but none of
 * AppLayout's desktop OS-shell machinery (deck windows, frozen locations,
 * taskbar dock).
 *
 * Layout (docs/plans/2026-07-06_MOBILE_HEADER_HOME.md): a FIXED header zone
 * (brand · app tabs · recents, capped at 30dvh, never scrolls) above a
 * content region that is the only vertical scroller. Login/signup/share-view
 * are top-level routes that match BEFORE the catch-all, so they keep their
 * desktop pages untouched.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTheme, getThemeClass, useToast, ThemedArea, ErrorBoundary, SettingsView, OrgCreateDialog, BudgetBillingView, budgetBillingPagePath, parseBudgetBillingPath, useShell, useBudgetBillingAccess, useMyCapabilities, DraftScopeProvider } from '@papan/core-ui/ui'
import { SingleFileView } from '@papan/workspace/ui'
import { DriveHomeView, RecentView, ProjectsView, SearchResultsView, NewProjectProvider, AppsView } from '@papan/files/ui'
import { filesApi } from '@papan/files/client'
import { parseMiniappIcon, appDisplayName } from '@papan/types'
import { orgsApi } from '@papan/orgs/client'
import { useConfirmBlockEndsConnection } from '@papan/org-connections/ui'
import { SpendingPage, BudgetsEditor, StorageUsagePage, FileActivityPage } from '@papan/orgs/ui'
import { BillingPage } from '@papan/billing/ui'
import { LeavingKeysNotice } from '@papan/keybook/ui'
import { MobileLauncherScreen } from './MobileLauncherScreen'
import { billingHandoff } from '../billingHandoff'
import { useChatHomeEnabled, useKeybookEnabled, useLandingExperience, useLandingPath, useLandingSettled } from '../lib/landing'
import { loginPathWithNext } from '../lib/session-loss'
import { useAuth } from '../hooks/useAuth'
import { useTenant } from '../hooks/useTenant'
import { useShellPersistence } from '../hooks/useShellPersistence'
import { MobileHeader } from './MobileHeader'
import { MobileChatScreen } from './MobileChatScreen'
import { MobileDriveScreen } from './MobileDriveScreen'
import { MobileAppScreen } from './MobileAppScreen'
import { MOBILE_APP_ROUTE_PATHS } from '../lib/app-deep-link'
import { MobileWorkspaceList } from './MobileWorkspaceList'
import { MobileWorkspaceScreen } from './MobileWorkspaceScreen'
import { MobileKeybookScreen } from './MobileKeybookScreen'
import { SessionDeck } from '../components/SessionDeck'
import { AppPanelProvider } from './appPanel'
import { activeTab } from './nav'
import { useIdentityOnly } from './useIdentityOnly'
import { ForgotPasswordModal } from '../components/ForgotPasswordModal'
import { IdentityOnlyNotice } from '../components/IdentityOnlyNotice'
import { KeybookDisabledNotice } from '../components/KeybookDisabledNotice'
import { IdentityShell } from '../IdentityShell'
import { IdentityRequiredDialog } from '../pages/IdentityRequiredDialog'
import { TenantAccessDeniedScreen } from '../pages/TenantAccessDeniedScreen'
import { useTenantDenied } from '../lib/tenant-denied'
import { decideIdentityShell } from '../lib/identityShell'
import { EntryCapture, EntryUnroutedMark } from '../lib/EntryCapture'
import { useOrgCreateLimit } from '../hooks/useOrgCreateLimit'
import { buildPlatformPages } from '../platformPages'
import { usePasswordActions } from '../lib/password'

export function MobileShell() {
  const { isAuthenticated, isLoading, signingOut, user, signOut, personalTenantId } = useAuth()
  const { tenantId, isPersonal, tenantSlug, activeOrgEmail, setActiveOrgEmail, isReady, switchTenant, orgEmailsHydrated, tenantChosen, markTenantChosen, entryPath } = useTenant()
  const navigate = useNavigate()
  const toast = useToast()
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const location = useLocation()
  const confirmBlockEndsConnection = useConfirmBlockEndsConnection()
  const prefix = isPersonal ? '/~' : `/t/${tenantId}`
  const page = activeTab(location.pathname)
  // The landing is a runtime platform setting. Subscribed rather than read so
  // the catch-all `<Navigate>` below re-renders if the value is corrected after
  // boot; `landingReady` gates the first render (see the early returns). The
  // shell is rendered TWICE — this file and App.tsx's AppLayout — and a
  // one-sided change is a phone that still flashes.
  // Mode-aware, exactly as desktop: an identity-only tenant lands on the
  // launcher whatever the deployment configured, because the chat home needs a
  // workspace this tenant can never have. `useLandingPath` routes through
  // `landingViewFor`, the one home of that override.
  const identityOnly = useIdentityOnly()
  const keybookEnabled = useKeybookEnabled()
  const chatOn = useChatHomeEnabled()
  const landingTarget = useLandingPath(prefix, identityOnly ? 'identity-only' : 'full')

  /** What a drive-family route renders when this tenant has no drive. */
  const noDrive = <IdentityOnlyNotice />
  const landingReady = useLandingSettled()

  /**
   * The identity shell on the phone (C2 / C3).
   *
   * This shell REPLACES AppLayout rather than nesting inside it, so none of the
   * desktop's shell logic reaches here — the picker and the org-less screen were
   * desktop-only for two commits because of exactly that. `decideIdentityShell`
   * is the shared rule; only the SOURCES differ (the phone reads the
   * `['mobile-my-tenants']` cache every other mobile screen already subscribes
   * to, so this costs no extra request).
   *
   * `isFetched` is the phone's `tenantsSettled`: true once the query has
   * resolved EITHER WAY, which is the distinction §9.1 turns on — an empty list
   * mid-flight is indistinguishable from a person with no organization.
   */
  const tenantsQuery = useQuery({
    queryKey: ['mobile-my-tenants'],
    queryFn: () => orgsApi.listMyTenants(),
    staleTime: 60_000,
  })
  const [showOrgCreate, setShowOrgCreate] = useState(false)
  const tenants = tenantsQuery.data?.tenants ?? []
  const settled = tenantsQuery.isFetched
  // Memoized like the desktop shell: an unmemoized decision is a fresh object
  // every render, and it feeds both the auto-switch effect's deps and
  // `invitesQuery`'s `enabled` — so the effect would re-run and the query
  // re-evaluate on every render (harmless but divergent and wasteful).
  // #1253: where this page load entered the app (`entryPath`, from useTenant —
  // a `/~/` deep link counts as a choice) and the landing view (which does not).
  const landingView = useLandingExperience()
  const shellDecision = useMemo(
    () => decideIdentityShell({ tenants, tenantId, tenantInUrl: Boolean(tenantSlug), entryPath, landingView, settled, chosen: tenantChosen }),
    [tenants, tenantId, tenantSlug, entryPath, landingView, settled, tenantChosen],
  )

  // Invites are an org-less person's ONLY route forward, and the phone had no
  // invite surface at all — so without this the org-less screen would be a dead
  // end there in a way it never is on desktop.
  const invitesQuery = useQuery({
    queryKey: ['mobile-my-invites'],
    queryFn: () => orgsApi.listMyInvites(),
    staleTime: 60_000,
    enabled: shellDecision.active,
  })
  const refetchIdentity = () => {
    void tenantsQuery.refetch()
    void invitesQuery.refetch()
  }
  const canCreateOrg = useOrgCreateLimit()

  // ── Tenant-scope recovery (desktop parity — see App.tsx) ─────────────
  // MobileShell REPLACES AppLayout, so none of the desktop's tenant-scope
  // recovery reaches the phone unless it is ported here — the same "shell
  // rendered twice" hazard that left the picker and the org-less screen
  // desktop-only for a commit. A deep link into `/t/<org>/…` for an org this
  // account belongs to under 2+ emails (both members) resolves to a 401
  // `org_email_required` on EVERY tenant-scoped call — INCLUDING `my-tenants`,
  // so `tenants` empties and the launcher's account pill vanishes with it. With
  // neither the picker below nor the desktop's, the phone had no way to choose
  // an identity and no way to switch tenant: a dead end escapable only by
  // hand-editing the URL back to the origin.

  // `org_email_required` (401) → IdentityRequiredDialog (pick which email).
  // Read off the SAME error the desktop reads from `listMyTenants().catch` — it
  // rides `tenantsQuery.error` here. Gated `!isPersonal && !activeOrgEmail`
  // exactly as App.tsx gates its copy: a resolved identity, or the personal
  // drive, is never ambiguous. `decideIdentityShell`'s picker cannot cover this
  // — it is suppressed by `tenantInUrl` and counts ORGS, not emails-in-one-org.
  const identityErr = tenantsQuery.error as
    | { code?: string; availableIdentities?: Array<{ email: string; role: string }>; tenantName?: string | null }
    | null
  const identityRequired =
    !isPersonal && !activeOrgEmail
    && identityErr?.code === 'org_email_required'
    && Array.isArray(identityErr.availableIdentities)
    && identityErr.availableIdentities.length > 0
      ? { identities: identityErr.availableIdentities, tenantName: identityErr.tenantName ?? null }
      : null

  // `tenant_access_denied` (403) → a full recovery screen (the sibling case: the
  // URL's tenant is one this account holds NO membership in — wrong account, or
  // a membership removed under a live tab). Subscribed via `onApiFailure`, the
  // same source App.tsx uses, because any tenant-scoped call can be the first to
  // reject. The tight status+code filter must not fire on #501's session-death
  // 401s, which carry no `code`.
  // The subscription, the reset-on-switch and (#853) a followed app link's
  // fallback to the reader's own way in — one hook, shared with the desktop shell.
  const [tenantDenied, setTenantDenied] = useTenantDenied(tenantId, personalTenantId)
  // A tenant that resolves is one we can reach — clear a stale verdict without
  // waiting for a navigation (e.g. membership restored mid-session).
  useEffect(() => { if (tenants.some(t => t.id === tenantId)) setTenantDenied(false) }, [tenants, tenantId])

  // Refetch the tenant list on every tenant change, exactly as AppLayout does
  // (its my-tenants fetch is keyed on `[tenantId]`, App.tsx). The phone reads a
  // STATIC-key cache (`['mobile-my-tenants']`, 60s stale, no refetch-on-switch),
  // so without this the two recovery paths above cannot self-heal the way their
  // desktop-copied comments claim: `tenants` never updates after a switch, so
  // the resolve-clear effect never sees the new tenant appear (a straggler 403
  // could re-stick the denied screen over a working personal drive), and
  // `onUsePersonal` would land on a personal drive whose org list is still the
  // errored/empty one. It also replaces a stale `org_email_required` error on a
  // switch, which is what keeps the `identityRequired` derivation below (not
  // tenant-keyed like desktop's) from ever showing another tenant's identities.
  // Guarded against the initial mount — the query fetches itself there, and a
  // forced refetch would only duplicate it (and re-run under StrictMode).
  const tenantRefetchRef = useRef(tenantId)
  useEffect(() => {
    if (tenantRefetchRef.current === tenantId) return
    tenantRefetchRef.current = tenantId
    void tenantsQuery.refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId])

  /**
   * Land somewhere workable, exactly as the desktop shell does.
   *
   * The phone never had this effect, so an identity-only person with ONE
   * organization sat on their driveless tenant indefinitely — no auto-switch
   * and, now, no picker either (one org is not a choice). That hole only became
   * visible once the other two states existed here. Same two guards as desktop:
   * `autoSwitchedRef` is what stops this and the switcher forming an infinite
   * loop, and `orgEmailsHydrated` stops the switch clobbering a stored identity
   * with the user's primary email.
   */
  const autoSwitchedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!orgEmailsHydrated || !identityOnly) return
    if (autoSwitchedRef.current === tenantId) return
    const target = shellDecision.autoSwitchTarget
    if (!target) return
    autoSwitchedRef.current = tenantId
    const email = target.emails.find((e) => e.email === user?.email)?.email ?? target.emails[0]?.email
    switchTenant(target.id, email)
  }, [orgEmailsHydrated, identityOnly, tenantId, shellDecision, user, switchTenant])

  // Hydrate the running-app set (+ pins) from /api/shell/instances on boot and
  // on identity switch — the SAME server-backed layer desktop's AppLayout uses
  // (App.tsx useShellPersistence) — so open mini-apps / standalone-HTML survive
  // a reload and re-appear in the launcher (MobileNavOverlay reads runningApps).
  // Mobile has no keep-alive deck windows, so we don't consume the returned
  // frozen routes, and pass deckKind:null to skip the system-app route
  // write-through (there are no hidden windows to restore a page into). User
  // app OPENS are persisted by MobileAppScreen (persistShellOpen), mirroring
  // desktop's RouteSync.
  useShellPersistence({
    tenantId,
    isReady,
    activeOrgEmail,
    deckKind: null,
    pathname: location.pathname,
    search: location.search,
  })

  // Identity switch: every running-app entry (and pin) is tenant-scoped, so
  // drop the previous identity's set the instant the tenant/email changes —
  // otherwise the launcher would flash the old tenant's app tiles until
  // hydration re-fetches. Mirrors AppLayout's tenant-switch reset (App.tsx).
  const { runningApps, openApp, resetApps, hydrateDock } = useShell()
  const resolvedRef = useRef<Set<string>>(new Set())
  const prevIdentityRef = useRef(`${tenantId}:${activeOrgEmail ?? ''}`)
  useEffect(() => {
    const key = `${tenantId}:${activeOrgEmail ?? ''}`
    if (prevIdentityRef.current === key) return
    prevIdentityRef.current = key
    resetApps(null)
    hydrateDock([], [])
    resolvedRef.current.clear() // re-resolve names/icons under the new identity
  }, [tenantId, activeOrgEmail, resetApps, hydrateDock])

  // Backfill restored apps' name + icon. Desktop resolves these via each
  // running app's keep-alive AppWindow (App.tsx onResolvedTitle/Icon); mobile
  // has no deck, so a hydrated-but-not-open app comes back title-less (the
  // /api/shell/instances row carries only kind+ref). Fetch each such app's
  // file info once and update the store so the launcher shows the real name +
  // squircle icon instead of "App" + the default glyph. MobileAppScreen still
  // owns the currently-open app's title/icon; this covers the rest.
  useEffect(() => {
    for (const a of runningApps) {
      if ((a.kind !== 'miniapp' && a.kind !== 'html') || a.title || resolvedRef.current.has(a.ref)) continue
      resolvedRef.current.add(a.ref)
      filesApi.getFileInfo(a.ref)
        .then((info) => {
          const icon = a.kind === 'miniapp'
            ? parseMiniappIcon((info as { metadataJson?: string | null }).metadataJson)
            : undefined
          openApp(a.kind, a.ref, appDisplayName(info.name), icon)
        })
        .catch(() => { resolvedRef.current.delete(a.ref) }) // allow a retry
    }
  }, [runningApps, openApp])

  // Same gates as AppLayout, in the same ORDER (its early returns live inside
  // the component, not above the route, so the mobile shell replicates them).
  const bootSkeleton = (
    <div className="h-dvh flex items-center justify-center bg-zinc-900">
      <div className="text-zinc-400">Loading...</div>
    </div>
  )
  if (isLoading) {
    return bootSkeleton
  }
  if (!isAuthenticated) {
    // Deliberate logout → clean /login; involuntary unauth keeps ?next=. Same as AppLayout.
    return <Navigate to={signingOut ? '/login' : loginPathWithNext(location.pathname + location.search + location.hash)} replace />
  }
  // Hold the same boot screen until we know which surface is home — the
  // catch-all route below navigates to it. See AppLayout's copy of this gate
  // for the full reasoning; the two must stay in step, because the shell is
  // rendered twice and fixing only the desktop leaves the phone flashing.
  //
  // ⚠ AFTER `isAuthenticated`, never folded into `isLoading`: the signed-out
  // path has no use for the landing, and making it wait turns an API outage
  // into an indefinite loading screen instead of a login form. And no timeout —
  // a timer firing on a slow network paints the seeded value, which is the
  // flash this gate exists to remove.
  if (!landingReady) {
    return bootSkeleton
  }
  const requireVerification = import.meta.env.VITE_REQUIRE_EMAIL_VERIFICATION === 'true'
  if (requireVerification && user && !user.emailVerified) {
    return <Navigate to={`/verify-email?email=${encodeURIComponent(user.email)}`} replace />
  }

  // The active tenant rejected us (403 `tenant_access_denied`). Every
  // tenant-scoped request is failing, so there is no working shell to render
  // behind a dialog — a full screen with the two real exits, exactly as
  // AppLayout does. Named only if the org is already in our own tenant list, so
  // this never becomes the enumeration oracle the server's 403 refused to be.
  if (tenantDenied) {
    return (
      <TenantAccessDeniedScreen
        tenantName={tenants.find((t) => t.id === tenantId)?.name ?? null}
        userEmail={user?.email ?? null}
        personal={tenantId.startsWith('p_')}
        onGoToMyDrive={() => {
          setTenantDenied(false)
          if (personalTenantId) void switchTenant(personalTenantId, undefined)
          else navigate('/~/folder')
        }}
        onSignOut={() => { void signOut() }}
      />
    )
  }

  // `tenantName` matters on an ORG: the picker labels the drive root
  // "‹name›'s Drive" and falls back to "My Drive" without it — which on a
  // phone was permanent, telling a member the ORGANISATION's drive was theirs.
  // Desktop passes it; this mount had been missed.
  return (
   <NewProjectProvider isOrg={!isPersonal} tenantId={tenantId} tenantName={tenants.find((t) => t.id === tenantId)?.name ?? undefined} currentUserEmail={activeOrgEmail || user?.email || ''}>
    <AppPanelProvider>
    {/* Installed-PWA (standalone) + viewport-fit=cover render the web view
        UNDER the status bar / home indicator, so the header used to collide
        with the clock + battery. Inset the shell by the safe-area env() values
        — the themed bg fills the insets, so the status-bar strip just reads as
        an extension of the header. (env() is 0 in a normal browser tab.) */}
    <div
      // Chat home keeps the desktop's ambient stage (violet ws-stage-chat)
      // behind the WHOLE shell — header included — instead of the opaque
      // primary surface the other tabs paint.
      className={`${themeClass} ${page === 'chat' ? 'ws-stage-chat' : 'themed-bg-primary'} themed-text h-dvh flex flex-col overflow-hidden`}
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <MobileHeader />
      {/* The identity shell (C2 / C3) — the SAME component the desktop shell
          mounts, not a phone lookalike. It portals to <body> and covers this
          tree, so its position here is immaterial; what matters is that the two
          shells cannot disagree about when it appears or what it offers.
          `settingsOpen` keys off the real route because mobile renders Settings
          as a route rather than a keep-alive deck. */}
      {shellDecision.active && user && (
        <IdentityShell
          user={{ name: user.name, email: user.email }}
          pickerTenants={shellDecision.pickerActive ? shellDecision.switchable : []}
          onPicked={markTenantChosen}
          activeTenantId={tenantId}
          activeOrgEmail={activeOrgEmail}
          pendingInvites={invitesQuery.data?.invites}
          onSwitchTenant={switchTenant}
          onCreateOrg={() => setShowOrgCreate(true)}
          canCreateOrg={canCreateOrg}
          onOpenSettings={() => navigate(`${prefix}/settings/profile`)}
          onSignOut={() => { void signOut() }}
          onAcceptInvite={async (token) => {
            // Feedback and error handling to match desktop — a silent no-op on a
            // failed accept leaves the person tapping with no explanation.
            try {
              await orgsApi.acceptInviteByToken(token)
              // The accepted org is a new switch target, and on this screen it is
              // what dismisses the shell.
              refetchIdentity()
              toast.success('Invitation accepted!')
            } catch (err: any) {
              // #1152: the invitation page asks for the code sent to the invited address.
              if (err?.code === 'INVITE_ADDRESS_PROOF_REQUIRED') { navigate(`/invite/${token}`); return }
              toast.error(err?.message || 'Failed to accept invite')
            }
          }}
          onDeclineInvite={async (token) => {
            try {
              await orgsApi.declineInvite(token)
              refetchIdentity()
              toast.success('Invitation declined')
            } catch (err: any) {
              toast.error(err?.message || 'Failed to decline invite')
            }
          }}
          onBlockInvite={async (token) => {
            try {
              // dw D6: blocking an org this person's drive is connected to also disconnects them.
              const invite = invitesQuery.data?.invites.find(i => i.inviteId === token)
              if (invite && !(await confirmBlockEndsConnection({ tenantId: invite.tenantId, personal: true }))) return
              await orgsApi.blockInvite(token)
              refetchIdentity()
              toast.success('Blocked invitations from this organization')
            } catch (err: any) {
              toast.error(err?.message || 'Failed to block invite')
            }
          }}
          settingsOpen={/\/settings(\/|$)/.test(location.pathname)}
          overlay={showOrgCreate ? (
            <OrgCreateDialog
              userEmail={user?.email}
              onCreated={(newTenantId, _name, ownerEmail) => {
                setShowOrgCreate(false)
                refetchIdentity()
                switchTenant(newTenantId, ownerEmail)
              }}
              onClose={() => setShowOrgCreate(false)}
            />
          ) : undefined}
        />
      )}
      {/* Identity picker — the org Drive can't resolve which of this account's
          emails to act as (2+ memberships, none selected). The SAME dialog the
          desktop mounts. Wrapped in a high stacking context because the dialog
          is z-50 and the mobile header is z-[60]: without this the header's
          brand/bell/burger would paint over the modal's backdrop. `relative` +
          z establishes the context so the `fixed` child paints above the
          header (and matches the launcher's z-[200]). */}
      {identityRequired && (
        <div className="relative z-[210]">
          <IdentityRequiredDialog
            tenantName={identityRequired.tenantName}
            identities={identityRequired.identities}
            onPick={async (email) => {
              await setActiveOrgEmail(email)
              // Selection is now stored server-side; reload so every query that
              // 401'd re-fires with the chosen identity (desktop does the same).
              window.location.reload()
            }}
            onUsePersonal={() => navigate('/~/folder')}
          />
        </div>
      )}
      {/* Content region — the ONLY vertical scroller lives inside each screen.
          `key={page}` re-triggers a light slide-in when the page (not the
          folder) changes. */}
      <div key={page} className="flex-1 min-h-0 flex flex-col mobile-page-in">
        {/* #1253: the page this load entered at — see desktop's EntryCapture. */}
        <EntryCapture shell="phone" />
        <Routes>
          {/* §8's empty state, on the phone. Same decision as desktop: the
              URL is LEFT ALONE rather than redirected, so a bookmark or a
              shared link says what happened instead of silently landing on
              the launcher. `noDrive` is spliced into each drive-family route
              below rather than wrapping the table, because these routes must
              still MATCH — falling through to the catch-all would redirect,
              which is the behaviour this is here to avoid. */}
          {/* #1316: the non-editor screens below sit in `SessionDeck` (remount at an identity / account end and a
              tenant switch; nothing while signing out). Chat, the single-file view, workspaces and apps hold
              editors and drafts, so they are never wrapped. */}
          {/* Chat home — the app's landing page (desktop: ChatHomeView).
              Swipe right = conversations drawer, swipe left = shortcuts.
              Not routed at all where chat is off (a launcher deployment,
              #368): the URL falls to the catch-all, which sends it home —
              the same as desktop. */}
          {chatOn && ['/chat', '/t/:tenant/chat', '/~/chat'].map((path) => (
            <Route key={path} path={path} element={<MobileChatScreen />} />
          ))}
          {/* Drive HOME — the same DriveHomeView desktop renders (greeting,
              search, suggested projects, Library quick access, recently
              modified). Its auto-fill grids collapse to one column at phone
              width, so it's reused as-is. */}
          {['/t/:tenant/home', '/~/home'].map((path) => (
            <Route key={path} path={path} element={identityOnly ? noDrive : <SessionDeck><MobileHomeScreen /></SessionDeck>} />
          ))}
          {/* #653 — Recent: the same page desktop renders. */}
          {['/t/:tenant/recent', '/~/recent'].map((path) => (
            <Route key={path} path={path} element={identityOnly ? noDrive : <MobileRecentScreen />} />
          ))}
          {/* Home's "View all" + Enter-to-search destinations — the same
              desktop views (both are plain scrollable lists/grids). */}
          {['/t/:tenant/projects', '/~/projects'].map((path) => (
            <Route key={path} path={path} element={identityOnly ? noDrive : <SessionDeck><MobileProjectsScreen /></SessionDeck>} />
          ))}
          {['/t/:tenant/pdapps', '/~/pdapps'].map((path) => (
            <Route key={path} path={path} element={identityOnly ? noDrive : <SessionDeck><MobileProjectsScreen variant="miniapp" /></SessionDeck>} />
          ))}
          {/* #1484: the PDApps shell — the same view as the desktop taskbar window. */}
          {['/t/:tenant/apps', '/~/apps', '/t/:tenant/apps/recent', '/~/apps/recent'].map((path) => (
            <Route key={path} path={path} element={<MobileAppsScreen historyOnly={identityOnly} />} />
          ))}
          {['/t/:tenant/search', '/~/search'].map((path) => (
            <Route key={path} path={path} element={identityOnly ? noDrive : <SessionDeck><MobileSearchScreen /></SessionDeck>} />
          ))}
          {/* Settings — the desktop SettingsView, URL-driven pages like
              desktop (/settings/:page/:param?/:subparam?). */}
          {[
            '/t/:tenant/settings/:page?/:param?/:subparam?',
            '/~/settings/:page?/:param?/:subparam?',
          ].map((path) => (
            <Route key={path} path={path} element={<SessionDeck><MobileSettingsScreen /></SessionDeck>} />
          ))}
          {/* Usage & Billing — moved out of Settings onto its own surface.
              `:sub?` is required, not decorative: the Tier-1 platform pages nest
              under a `platform/` segment (`/billing/platform/tenants`,
              `/billing/platform/pricing`), so a single-segment route matched
              NEITHER and both fell through to the catch-all — a support link
              pasted on a phone landed on chat while desktop resolved it fine.
              That undercut the whole reason those pages were given linkable
              paths. `parseBudgetBillingPath` already reads both segments; only
              the route shape was missing. Same `:page?/:param?` shape the
              settings routes above use. */}
          {['/t/:tenant/billing/:page?/:sub?/:subsub?', '/~/billing/:page?/:sub?/:subsub?'].map((path) => (
            <Route key={path} path={path} element={<SessionDeck><MobileBillingScreen /></SessionDeck>} />
          ))}
          {['/t/:tenant/folder/:folderId', '/~/folder/:folderId', '/t/:tenant/folder', '/~/folder'].map((path) => (
            <Route key={path} path={path} element={identityOnly ? noDrive : <SessionDeck><MobileDriveScreen /></SessionDeck>} />
          ))}
          {/* File taps open the same standalone viewer route as desktop
              (images / pdf / office / text all resolve through it). */}
          {['/file/:fileId', '/~/file/:fileId', '/t/:tenant/file/:fileId'].map((path) => (
            <Route key={path} path={path} element={
              <ThemedArea area="workspace" className="mobile-file-in flex-1 flex min-w-0 min-h-0 pt-3">
                <ErrorBoundary>
                  <DraftScopeProvider scope="single"><SingleFileView tenantId={tenantId} mobile /></DraftScopeProvider>
                </ErrorBoundary>
              </ThemedArea>
            } />
          ))}
          {['/t/:tenant/workspace', '/~/workspace'].map((path) => (
            <Route key={path} path={path} element={identityOnly ? noDrive : <SessionDeck><MobileWorkspaceList /></SessionDeck>} />
          ))}
          {/* The real workspace surface — Files / Editor / Assistant sections
              (docs/plans/2026-07-07_MOBILE_WORKSPACE_SURFACE.md). */}
          {['/t/:tenant/workspace/:workspaceId', '/~/workspace/:workspaceId'].map((path) => (
            <Route key={path} path={path} element={identityOnly ? noDrive : <MobileWorkspaceScreen />} />
          ))}
          {['/keybook', '/~/keybook', '/t/:tenant/keybook'].map((path) => (
            // 0179: a keybook-disabled deployment refuses the stale URL in
            // words, same as identity-only does for its missing surfaces.
            <Route key={path} path={path} element={identityOnly ? noDrive : keybookEnabled ? <SessionDeck><MobileKeybookScreen /></SessionDeck> : <KeybookDisabledNotice />} />
          ))}
          {/* Run a `.miniapp` — the phone equivalent of desktop's AppWindow
              (`/app/:id`). Runs the app in the chromeless MiniAppRunView with
              a mobile back/title bar; without this, `/app/:id` navs fell
              through to the catch-all and silently redirected to /chat. */}
          {/* `/*` carries a page under the app (`/app/<id>/<page>?…#…`, the
              desktop route's shape) — `pdapp.links.page()`'s Papan address opens
              the same page on a phone. */}
          {MOBILE_APP_ROUTE_PATHS.map((path) => (
            <Route key={path} path={path} element={<MobileAppScreen />} />
          ))}
          {/* The curated app grid. MUST exist wherever the landing can resolve
              to it: the catch-all below navigates to the landing, so a launcher
              deployment without this route would bounce a phone from the
              landing straight back to the landing. */}
          {['/launcher', '/~/launcher', '/t/:tenant/launcher'].map((path) => (
            <Route key={path} path={path} element={<MobileLauncherScreen />} />
          ))}
          {/* Everything else (bin, quarantine, …) has no mobile surface yet —
              land on this deployment's home (desktop parity, same resolver).
              `/` lands here too — it is every sign-in flow's fallback
              (`safeNext`) — so, like desktop's `/` and `*`, wait for
              `my-tenants`: `identityOnly` is false until it answers, and a
              one-shot `replace` taken before that sends an identity-only
              phone to chat. `settled` is `isFetched`, true on error too, so
              this cannot hang online (offline the query pauses; it resumes on
              reconnect). */}
          {/* EntryUnroutedMark — see desktop's catch-all (#1253). */}
          <Route path="*" element={<><EntryUnroutedMark shell="phone" />{settled ? <Navigate to={landingTarget} replace /> : bootSkeleton}</>} />
        </Routes>
      </div>
    </div>
    </AppPanelProvider>
   </NewProjectProvider>
  )
}

/** Shared identity bits the reused desktop drive views take as props. */
function useDriveViewProps() {
  const { user } = useAuth()
  const { tenantId, isPersonal, activeOrgEmail } = useTenant()
  const prefix = isPersonal ? '/~' : `/t/${tenantId}`
  const tenantsQuery = useQuery({
    queryKey: ['mobile-my-tenants'],
    queryFn: () => orgsApi.listMyTenants(),
    staleTime: 60_000,
  })
  const tenantName = isPersonal
    ? undefined // personal: DriveHomeView hides the "…in <tenant>" subtitle
    : tenantsQuery.data?.tenants.find((t) => t.id === tenantId)?.name
  return { prefix, isOrg: !isPersonal, orgEmail: activeOrgEmail, userName: user?.name, tenantName }
}

function MobileHomeScreen() {
  const p = useDriveViewProps()
  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <DriveHomeView
        urlPrefix={p.prefix}
        isOrg={p.isOrg}
        orgEmail={p.orgEmail}
        userName={p.userName}
        tenantName={p.tenantName}
      />
    </div>
  )
}

function MobileRecentScreen() {
  const p = useDriveViewProps()
  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <RecentView urlPrefix={p.prefix} isOrg={p.isOrg} orgEmail={p.orgEmail ?? undefined} tenantName={p.tenantName} />
    </div>
  )
}

function MobileProjectsScreen({ variant }: { variant?: 'project' | 'miniapp' }) {
  const p = useDriveViewProps()
  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <ProjectsView urlPrefix={p.prefix} isOrg={p.isOrg} orgEmail={p.orgEmail} tenantName={p.isOrg ? p.tenantName : p.userName} variant={variant} />
    </div>
  )
}

function MobileAppsScreen({ historyOnly = false }: { historyOnly?: boolean }) {
  const p = useDriveViewProps()
  const { tenantId, chooseIdentityIfUnset } = useTenant()
  return (
    <ThemedArea area="workspace" className="flex-1 min-h-0 flex flex-col overflow-hidden">
      <ErrorBoundary>
        <AppsView key={tenantId} urlPrefix={p.prefix} tenantId={tenantId} isOrg={p.isOrg} orgEmail={p.orgEmail} orgName={p.isOrg ? p.tenantName : p.userName} historyOnly={historyOnly} chooseIdentityIfUnset={chooseIdentityIfUnset} />
      </ErrorBoundary>
    </ThemedArea>
  )
}

function MobileSearchScreen() {
  const p = useDriveViewProps()
  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <SearchResultsView urlPrefix={p.prefix} />
    </div>
  )
}

/**
 * Settings — the desktop SettingsView with a lean prop set (identity +
 * name/password/sign-out handlers, same impls as App.tsx). Desktop-only
 * wiring (org delete, file picker, keybook/connections render props) is
 * omitted for now — those pages render read-only or hide.
 */
function MobileSettingsScreen() {
  const passwordActions = usePasswordActions()
  const identityOnlySettings = useIdentityOnly()
  const { user, signOut, refreshSession } = useAuth()
  const { tenantId, isPersonal, activeOrgEmail } = useTenant()
  const navigate = useNavigate()
  const prefix = isPersonal ? '/~' : `/t/${tenantId}`
  const { page, param, subparam } = useParams<{ page?: string; param?: string; subparam?: string }>()
  const tenantsQuery = useQuery({
    queryKey: ['mobile-my-tenants'],
    queryFn: () => orgsApi.listMyTenants(),
    staleTime: 60_000,
  })

  /**
   * email → the organizations it carries access to.
   *
   * Desktop has always passed this (`App.tsx`); mobile never did, so on a phone
   * "Remove email" asked for confirmation WITHOUT the line listing what access
   * goes with it — the one fact that decides the answer. Silently losing org
   * access tied to an address is the whole of #351; asking someone to confirm it
   * blind is the same outcome with a button in front of it.
   *
   * Free: the query above is already fetched for this screen, so this adds no
   * request. Same shape as the desktop map, so `SettingsView` sees one thing.
   */
  const orgsByEmail = useMemo(() => {
    const map: Record<string, Array<{ orgName: string; role: string }>> = {}
    for (const t of tenantsQuery.data?.tenants ?? []) {
      if (t.type !== 'organization') continue
      for (const e of t.emails) {
        const key = e.email.toLowerCase()
        if (!map[key]) map[key] = []
        map[key].push({ orgName: t.name, role: e.role })
      }
    }
    return map
  }, [tenantsQuery.data])
  const tenant = tenantsQuery.data?.tenants.find((t) => t.id === tenantId)
  const email = activeOrgEmail ?? user?.email
  // Settings' password escape hatch — see ForgotPasswordModal for why this is
  // shown in place rather than routed to. Mobile DOES remount on navigation, so
  // the deck-state argument does not apply here; the other two do, and the
  // stranding one lands hardest on exactly this surface (an iOS home-screen
  // install has no back button).
  const [forgotPwOpen, setForgotPwOpen] = useState(false)
  const userRole = tenant && tenant.type !== 'personal'
    ? (tenant.emails.find((e) => e.email === email)?.role ?? tenant.emails[0]?.role)
    : undefined
  // Billing authority — the `manage_billing` capability, matching the server
  // gate. Moved up from the billing screen with the pages themselves; personal
  // tenants short-circuit, since a personal owner is an absolute floor in the
  // resolver and the capability query only runs for org tenants.
  const { can: canCap, loading: capsLoading } = useMyCapabilities(tenantId, isPersonal, activeOrgEmail)
  const canManageBilling = isPersonal || canCap('manage_billing', 'write')
  /** Whether the Billing screen exists for this caller — READ, where the
   *  controls take write. Must match App.tsx: both shells mount this page, and
   *  the last time the two diverged every mobile deep link opened the wrong
   *  one. See the long note at App.tsx's own derivation for why an org's
   *  billing is not member business, and why the routes refuse it too. */
  const canViewBilling = isPersonal || canCap('manage_billing', 'read')

  return (
    <ThemedArea area="workspace" className="flex-1 flex min-w-0 min-h-0">
      <SettingsView
        orgsByEmail={orgsByEmail}
        renderLeaveNotice={(tid) => <LeavingKeysNotice tenantId={tid} />}
          identityOnly={identityOnlySettings}
        mobile
        onClose={() => navigate(-1)}
        user={user ?? undefined}
        onSignOut={() => { void signOut() }}
        activePage={page}
        activeParam={param}
        activeSubparam={subparam}
        onPageChange={(pg, pa, sub) => {
          let url = `${prefix}/settings/${pg}`
          if (pa) url += `/${pa}`
          if (sub) url += `/${sub}`
          navigate(url)
        }}
        hasPassword={passwordActions.hasPassword}
        onSetPassword={passwordActions.onSetPassword}
        onChangePassword={passwordActions.onChangePassword}
        onForgotPassword={() => setForgotPwOpen(true)}
        onRetryHasPassword={passwordActions.onRetryHasPassword}
        onUpdateName={async (newName) => {
          const trimmed = newName.trim()
          if (!trimmed) return { error: 'Name cannot be empty.' }
          const { authClient } = await import('@papan/auth/client')
          const result = await authClient.updateUser({ name: trimmed })
          if (result.error) return { error: result.error.message || 'Failed to update name' }
          void refreshSession().catch(() => {})
          return {}
        }}
        isOrg={!isPersonal}
        tenantId={tenantId}
        tenantName={tenant?.name}
        // Mobile reads the name from a cached query (60s staleTime), not local
        // state as App.tsx does — so a rename must REFETCH here, or the old
        // name survives for up to a minute after the server accepted it.
        onRenameOrg={() => { void tenantsQuery.refetch() }}
        userRole={userRole}
        orgEmail={activeOrgEmail}
        // Must match App.tsx — both shells mount this and both previously
        // omitted `personal`, so the org waterfall was what every solo user
        // saw on either.
        
        // This tenant's OWN spend, back from the Usage & Billing shelf now that
        // the shelf is platform-only. Must match App.tsx — both shells mount
        // this view, and the last time they diverged every mobile deep link
        // opened the wrong page.
        budgetPage={<SpendingPage tenantId={tenantId} userEmail={email} personal={isPersonal} onNavigate={() => navigate(`${prefix}/settings/limits`)} onOpenBilling={() => navigate(`${prefix}/settings/subscription`)} />}
        limitsPage={<BudgetsEditor tenantId={tenantId} userEmail={email} onNavigate={() => navigate(`${prefix}/settings/budget`)} />}
        // Must match App.tsx — both shells mount this view (see the budgetPage
        // note above for what diverging cost last time).
        storagePage={<StorageUsagePage tenantId={tenantId} personal={isPersonal} userEmail={email} onOpenBin={() => navigate(`${prefix}/bin`)} onOpenQuarantine={() => navigate(`${prefix}/quarantine`)} onRevealFile={(folderId, fileId) => {
          const base = folderId && !folderId.startsWith('papan_') ? `${prefix}/folder/${folderId}` : `${prefix}/folder`
          navigate(`${base}?reveal=${fileId}`)
        }} onOpenFolder={(folderId) => navigate(`${prefix}/folder/${folderId}`)} onOpenSubscription={() => navigate(`${prefix}/settings/subscription`)} />}
        // Settings → Organization → File activity (#118, Task B8). Must match
        // App.tsx — see the budgetPage note above for what diverging cost.
        fileActivityPage={<FileActivityPage tenantId={tenantId} userEmail={email} />}
        // See App.tsx: `null` is a resolved denial, the flag is the in-flight
        // window. Both shells must agree or one device shows a false refusal.
        billingPageLoading={capsLoading}
        billingPage={!canViewBilling ? null : (
          // Identical derivation to desktop, from the same helper. These were
          // two inline expressions that disagreed: this one keyed off
          // `canManageBilling`, desktop off `activeRole`. Same question, two
          // answers, and the difference was visible to anyone holding
          // `manage_billing:write` without an admin role.
          <BillingPage
            canManageBilling={canManageBilling}
            {...billingHandoff({
              isPersonal,
              canReadBudget: canCap('manage_budget', 'read'),
              canManageBilling,
              prefix,
              navigate,
            })}
          />
        )}
      />
      {forgotPwOpen && (
        <ForgotPasswordModal
          email={user?.email}
          onClose={() => setForgotPwOpen(false)}
        />
      )}
    </ThemedArea>
  )
}

/**
 * Usage & Billing — the PLATFORM shelf (tenant ceilings, feature pricing,
 * catalog). Desktop reaches it from the taskbar shelf button; mobile from the
 * `/billing/:page` route behind the header burger menu.
 */
function MobileBillingScreen() {
  const { user } = useAuth()
  const { tenantId, isPersonal } = useTenant()
  const navigate = useNavigate()
  const routerLocation = useLocation()
  const prefix = isPersonal ? '/~' : `/t/${tenantId}`
  const budgetBilling = useBudgetBillingAccess({
    tenantId, platformRole: user?.platformRole,
  })
  // Parsed from the ROUTER's location, and from the path rather than the
  // `:page` param. The param cannot tell `/billing/platform` from
  // `/billing/platform/pricing` (the pages nest under a `platform/` segment),
  // and the global `location` this used to read does not re-render on a
  // client-side navigation — now that the parse also decides a redirect, a
  // stale read would send the viewer to the wrong place.
  const route = parseBudgetBillingPath(routerLocation.pathname)

  // Route guard, not just a hidden button: anyone who types /billing (or
  // follows an old link) must land somewhere real, not on an empty shelf.
  //
  // The moved-page redirect is unconditional; the ACCESS one waits for
  // `loading`, because while a gate's fetch is in flight `allowed` is false for
  // the same reason a denial is, and redirecting on that bounced every cold
  // load of a /billing URL straight to Settings. The platform predicate needs
  // no fetch today, but the guard is what keeps that from regressing.
  // The query string comes along: a Stripe Checkout session created before this
  // move returns to `/billing/subscription?checkout=success`, and BillingPage
  // reads that param to tell a completed purchase from an abandoned one.
  if (route.settingsPage) {
    return <Navigate to={`${prefix}/settings/${route.settingsPage}${routerLocation.search}`} replace />
  }
  if (budgetBilling.loading) return null
  if (!budgetBilling.allowed) {
    return <Navigate to={`${prefix}/settings/usage`} replace />
  }

  return (
    <ThemedArea area="workspace" className="flex-1 flex min-w-0 min-h-0">
      <BudgetBillingView
        mobile
        onClose={() => navigate(-1)}
        activePage={route.page}
        onPageChange={(pg) => navigate(`${prefix}/billing/${budgetBillingPagePath(pg)}`)}
        capabilities={budgetBilling.capabilities}
        {...buildPlatformPages({
          access: budgetBilling,
          isPlatformAdmin: user?.platformRole === 'admin',
          activeTenantId: tenantId,
          customerId: route.tenantId,
          planId: route.planId,
          onOpenCustomer: (id) => navigate(`${prefix}/billing/${budgetBillingPagePath('customers')}/${id}`),
          onCloseCustomer: () => navigate(`${prefix}/billing/${budgetBillingPagePath('customers')}`),
          onOpenPlan: (id) => navigate(`${prefix}/billing/${budgetBillingPagePath('pricebook')}/${id}`),
          onClosePlan: () => navigate(`${prefix}/billing/${budgetBillingPagePath('pricebook')}`),
          onOpenOperations: () => navigate(`${prefix}/billing/${budgetBillingPagePath('operations')}`),
          onOpenSettings: () => navigate(`${prefix}/billing/${budgetBillingPagePath('settings')}`),
        })}
      />
    </ThemedArea>
  )
}

